import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp } from "./Motion";
import { Landmark, Building2, LineChart } from "lucide-react";

const audiences = [
  {
    id: "nbfcs",
    label: "For NBFCs",
    icon: Building2,
    headline: "Underwrite thin-file MSMEs with confidence.",
    points: [
      "Two-pillar verified-data assessment (Financial + Operational) that plugs directly into existing credit workflows.",
      "GST compliance and trading-relationship verification give faster clarity on new-to-credit and thin-file applicants.",
      "Serve more first-time borrowers confidently — expanding your addressable market without loosening risk discipline.",
    ],
  },
  {
    id: "banks",
    label: "For Banks",
    icon: Landmark,
    headline: "Complement CIBIL and collateral with verified operating data.",
    points: [
      "Layer commercial credit history and buyer/supplier-network analysis on top of your existing CIBIL and collateral-based assessment.",
      "Reduce false rejections of creditworthy MSMEs by seeing verified operational signals the credit file can't show.",
      "Support priority-sector lending targets with sharper risk visibility and an auditable, API-driven decision trail.",
    ],
  },
  {
    id: "investors",
    label: "For Investors",
    icon: LineChart,
    headline: "Standardised financial and operational diligence.",
    points: [
      "Verify founder claims with GST filing patterns, revenue trends, and bank-statement analysis — not spreadsheets.",
      "Spot red flags in operational health early — customer concentration, supplier dependency, and compliance gaps.",
      "Track portfolio health on a common scoring framework across every MSME you back.",
    ],
  },
];

export const Audiences = () => {
  const [active, setActive] = useState("nbfcs");
  const current = audiences.find((a) => a.id === active);

  return (
    <section id="audience-value" className="relative py-24 md:py-36 border-t border-ink/10 bg-navy text-paper overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(250,250,250,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,250,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className="flex items-center gap-4 md:gap-6 mb-16 flex-wrap">
            <span className="font-mono-cap text-paper/60">§ 07 — Value by Audience</span>
            <span className="flex-1 min-w-8 h-px bg-paper/20" />
          </div>
        </FadeUp>

        <FadeUp>
          <h2 className="font-display text-5xl md:text-7xl text-paper leading-[0.98] max-w-4xl">
            Find the value <span className="italic text-teal">built for you</span>.
          </h2>
        </FadeUp>

        {/* Tabs */}
        <div className="mt-14 flex flex-wrap gap-3">
          {audiences.map((a) => {
            const Icon = a.icon;
            const isActive = active === a.id;
            return (
              <button
                key={a.id}
                data-testid={`audience-tab-${a.id}`}
                onClick={() => setActive(a.id)}
                className={`inline-flex items-center gap-3 px-5 py-3 border font-mono-cap transition-colors duration-300 ${
                  isActive
                    ? "bg-teal border-teal text-paper"
                    : "bg-transparent border-paper/25 text-paper/70 hover:border-paper/60"
                }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                {a.label}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div className="mt-10 border border-paper/15 bg-navy-soft/40">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              data-testid={`audience-panel-${current.id}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-8 md:gap-12 p-8 md:p-14"
            >
              <div className="col-span-12 lg:col-span-5">
                <div className="w-12 h-12 flex items-center justify-center bg-teal text-paper">
                  <current.icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-3xl md:text-5xl text-paper mt-8 leading-[1.02]">
                  {current.headline}
                </h3>
              </div>
              <div className="col-span-12 lg:col-span-7">
                <div className="space-y-6">
                  {current.points.map((p, i) => (
                    <div key={i} className="flex gap-5 border-t border-paper/15 pt-5">
                      <span className="font-mono-cap text-teal shrink-0">
                        0{i + 1}
                      </span>
                      <p className="font-sans text-paper/80 leading-relaxed text-base md:text-lg">
                        {p}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
